import { Address, Chain, defineChain } from "viem"
import {
  arbitrum,
  avalanche,
  base,
  bsc,
  foundry,
  linea,
  mainnet,
  mantle,
  sepolia,
} from "viem/chains"

import { SupportedChainId } from "./config"

// Here you can check the configurations
// https://github.com/wevm/viem/tree/7634a9f25d3ea0970751ffe5aad8fdf6d9235ef8/src/chains/definitions

export const berachain = /*#__PURE__*/ defineChain({
  id: 80094,
  name: "Berachain",
  nativeCurrency: {
    decimals: 18,
    name: "BERA Token",
    symbol: "BERA",
  },
  blockExplorers: {
    default: {
      name: "Berascan",
      url: "https://berascan.com",
    },
  },
  rpcUrls: {
    default: { http: ["https://rpc.berachain.com"] },
  },
  contracts: {
    multicall3: {
      address: "0xcA11bde05977b3631167028862bE2a173976CA11",
      blockCreated: 0,
    },
  },
} as const)

export const swell = /*#__PURE__*/ defineChain({
  id: 1923,
  name: "Swellchain",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: {
      http: [
        "https://swell-mainnet.alt.technology",
        "https://rpc.ankr.com/swell",
      ],
    },
  },
  blockExplorers: {
    default: {
      name: "Swell Explorer",
      url: "https://explorer.swellnetwork.io",
      apiUrl: "https://explorer.swellnetwork.io/api",
    },
  },
  contracts: {
    multicall3: {
      address: "0xcA11bde05977b3631167028862bE2a173976CA11",
      blockCreated: 1,
    },
  },
} as const)

export const sonic = /*#__PURE__*/ defineChain({
  id: 146,
  name: "Sonic",
  nativeCurrency: {
    decimals: 18,
    name: "Sonic",
    symbol: "S",
  },
  blockExplorers: {
    default: {
      name: "Sonicscan",
      url: "https://sonicscan.org/",
    },
  },
  rpcUrls: {
    default: {
      http: ["https://rpc.soniclabs.com"],
    },
  },
  contracts: {
    multicall3: {
      address: "0xcA11bde05977b3631167028862bE2a173976CA11",
      blockCreated: 0,
    },
  },
} as const)

export const unichain = /*#__PURE__*/ defineChain({
  id: 130,
  name: "Unichain",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: {
      http: ["https://mainnet.unichain.org/"],
    },
  },
  blockExplorers: {
    default: {
      name: "Uniscan",
      url: "https://uniscan.xyz",
      apiUrl: "https://api.uniscan.xyz/api",
    },
  },
  contracts: {
    multicall3: {
      address: "0xca11bde05977b3631167028862be2a173976ca11",
      blockCreated: 0,
    },
  },
} as const)

export const ink = /*#__PURE__*/ defineChain({
  id: 57073,
  name: "Ink",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: {
      http: [
        "https://rpc-gel.inkonchain.com",
        "https://rpc-qnd.inkonchain.com",
      ],
      webSocket: [
        "wss://rpc-gel.inkonchain.com",
        "wss://rpc-qnd.inkonchain.com",
      ],
    },
  },
  blockExplorers: {
    default: {
      name: "Blockscout",
      url: "https://explorer.inkonchain.com",
      apiUrl: "https://explorer.inkonchain.com/api/v2",
    },
  },
  contracts: {
    multicall3: {
      address: "0xcA11bde05977b3631167028862bE2a173976CA11",
      blockCreated: 6644075,
    },
  },
} as const)

export const bob = /*#__PURE__*/ defineChain({
  id: 60808,
  name: "BOB",
  nativeCurrency: {
    decimals: 18,
    name: "ETH",
    symbol: "ETH",
  },
  rpcUrls: {
    default: {
      http: ["https://rpc.gobob.xyz"],
      webSocket: ["wss://rpc.gobob.xyz"],
    },
  },
  blockExplorers: {
    default: {
      name: "BOB Explorer",
      url: "https://explorer.gobob.xyz",
    },
  },
  contracts: {
    multicall3: {
      address: "0xcA11bde05977b3631167028862bE2a173976CA11",
      blockCreated: 23131,
    },
  },
} as const)

export const hyperevm = /*#__PURE__*/ defineChain({
  id: 999,
  name: "HyperEVM",
  nativeCurrency: {
    decimals: 18,
    name: "HYPE",
    symbol: "HYPE",
  },
  rpcUrls: {
    default: { http: ["https://rpc.hyperliquid.xyz/evm"] },
  },
  blockExplorers: {
    default: {
      name: "Purrsec",
      url: "https://purrsec.com",
    },
  },
  contracts: {
    multicall3: {
      address: "0xcA11bde05977b3631167028862bE2a173976CA11",
      blockCreated: 13051,
    },
  },
  testnet: false,
} as const)

export const tac = /*#__PURE__*/ defineChain({
  id: 239,
  name: "TAC",
  nativeCurrency: {
    decimals: 18,
    name: "TAC",
    symbol: "TAC",
  },
  blockExplorers: {
    default: {
      name: "TAC Explorer",
      url: "https://explorer.tac.build/",
    },
  },
  rpcUrls: {
    default: {
      http: ["https://rpc.tac.build"],
    },
  },
  contracts: {
    multicall3: {
      address: "0xca11bde05977b3631167028862be2a173976ca11",
      blockCreated: 0,
    },
  },
} as const)

export const plasma = /*#__PURE__*/ defineChain({
  id: 9745,
  name: "Plasma",
  nativeCurrency: {
    decimals: 18,
    name: "XPL",
    symbol: "XPL",
  },
  blockExplorers: {
    default: {
      name: "Plasma Explorer",
      url: "https://plasmascan.to/",
    },
  },
  rpcUrls: {
    default: {
      http: ["https://rpc.plasma.to"],
    },
  },
  contracts: {
    multicall3: {
      address: "0xcA11bde05977b3631167028862bE2a173976CA11",
      blockCreated: 0,
    },
  },
} as const)

export const monad = /*#__PURE__*/ defineChain({
  id: 143,
  name: "Monad",
  nativeCurrency: {
    decimals: 18,
    name: "MONAD",
    symbol: "MON",
  },
  blockExplorers: {
    default: {
      name: "Monad Explorer",
      url: "https://monadvision.com",
    },
  },
  rpcUrls: {
    default: {
      http: ["https://rpc3.monad.xyz"],
    },
  },
  contracts: {
    multicall3: {
      address: "0xca11bde05977b3631167028862be2a173976ca11",
      blockCreated: 9248132,
    },
  },
} as const)

//////////////////////
// TESTNETS
//////////////////////

export const tenderly = /*#__PURE__*/ defineChain({
  id: 41_337,
  name: "Tenderly",
  nativeCurrency: {
    decimals: 18,
    name: "Ether",
    symbol: "ETH",
  },
  blockExplorers: {
    default: {
      name: "Tenderly",
      url: "https://dashboard.tenderly.co/euler-labs/euler/testnet/988d1fae-2b91-4b12-b166-488344404134", //TODO: extract to env var
    },
  },
  rpcUrls: {
    default: {
      http: ["http://127.0.0.1:8545"],
      webSocket: ["ws://127.0.0.1:8545"],
    },
  },
})

export const bartio = /*#__PURE__*/ defineChain({
  id: 80084,
  name: "Bartio",
  nativeCurrency: {
    decimals: 18,
    name: "Bera",
    symbol: "BERA",
  },
  blockExplorers: {
    default: {
      name: "Bartio",
      url: "https://bartio.beratrail.io/",
    },
  },
  rpcUrls: {
    default: {
      http: ["https://bartio.rpc.berachain.com/"],
    },
  },
} as const)

//////////////////////

export const chainLogos: { [key: number]: string } = {
  [mainnet.id]: "/network/ethereum.png",
  [sepolia.id]: "/network/ethereum.png",
  [arbitrum.id]: "/network/arbitrum.png",
  [bartio.id]: "/network/bartio.png",
  [base.id]: "/network/base.png",
  [swell.id]: "/network/swell.png",
  [sonic.id]: "/network/sonic.png",
  [unichain.id]: "/network/unichain.svg",
  [avalanche.id]: "/network/avax.png",
  [berachain.id]: "/network/berachain.png",
  [ink.id]: "/network/ink.png",
  [bob.id]: "/network/bob.png",
  [bsc.id]: "/network/binance.svg",
  [hyperevm.id]: "/network/hyperevm.svg",
  [tac.id]: "/network/tac.png",
  [linea.id]: "/network/linea.png",
  [mantle.id]: "/network/mantle.png",
  [plasma.id]: "/network/plasma.png",
  [monad.id]: "/network/monad.svg",
}

export type TokenListItem = {
  addressInfo: Address
  chainId: number
  decimals: number
  logoURI: string
  name: string
  symbol: string
  meta?: {
    doesNotAutoCompound?: boolean
    claimUrl?: string
    provider?: string
    // DEFILLAMA
    poolId?: string & { provider: "DEFILLAMA" }
    // PENDLE
    isPendlePT?: boolean
    isPendleCrossChainPT?: boolean
    pendleMarket?: string & { isPendlePT: true }
    // SPECTRA
    isSpectraPT?: boolean
    spectraPool?: string & { isSpectraPT: true }
  }
}

export const chainIdToChain: Record<SupportedChainId, Chain> = {
  [mainnet.id]: mainnet,
  [sepolia.id]: sepolia,
  [arbitrum.id]: arbitrum,
  [bartio.id]: bartio,
  [base.id]: base,
  [swell.id]: swell,
  [sonic.id]: sonic,
  [unichain.id]: unichain,
  [avalanche.id]: avalanche,
  [berachain.id]: berachain,
  [ink.id]: ink,
  [bob.id]: bob,
  [bsc.id]: bsc,
  [tenderly.id]: tenderly,
  [foundry.id]: foundry,
  [hyperevm.id]: hyperevm,
  [tac.id]: tac,
  [linea.id]: linea,
  [mantle.id]: mantle,
  [plasma.id]: plasma,
  [monad.id]: monad,
} as const

export const RPC_URLS: Record<SupportedChainId, string> = {
  [mainnet.id]: process.env["NEXT_PUBLIC_RPC_HTTP_1"] || "",
  [base.id]: process.env["NEXT_PUBLIC_RPC_HTTP_8453"] || "",
  [swell.id]: process.env["NEXT_PUBLIC_RPC_HTTP_1923"] || "",
  [sonic.id]: process.env["NEXT_PUBLIC_RPC_HTTP_146"] || "",
  [avalanche.id]: process.env["NEXT_PUBLIC_RPC_HTTP_43114"] || "",
  [arbitrum.id]: process.env["NEXT_PUBLIC_RPC_HTTP_42161"] || "",
  [ink.id]: process.env["NEXT_PUBLIC_RPC_HTTP_57073"] || "",
  [unichain.id]: process.env["NEXT_PUBLIC_RPC_HTTP_130"] || "",
  [berachain.id]: process.env["NEXT_PUBLIC_RPC_HTTP_80094"] || "",
  [bob.id]: process.env["NEXT_PUBLIC_RPC_HTTP_60808"] || "",
  [tenderly.id]: process.env["NEXT_PUBLIC_RPC_HTTP_41337"] || "",
  [bartio.id]: process.env["NEXT_PUBLIC_RPC_HTTP_80084"] || "",
  [sepolia.id]: process.env["NEXT_PUBLIC_RPC_HTTP_11155111"] || "",
  [foundry.id]: process.env["NEXT_PUBLIC_RPC_HTTP_31337"] || "",
  [bsc.id]: process.env["NEXT_PUBLIC_RPC_HTTP_56"] || "",
  [hyperevm.id]: process.env["NEXT_PUBLIC_RPC_HTTP_999"] || "",
  [tac.id]: process.env["NEXT_PUBLIC_RPC_HTTP_239"] || "",
  [linea.id]: process.env["NEXT_PUBLIC_RPC_HTTP_59144"] || "",
  [mantle.id]: process.env["NEXT_PUBLIC_RPC_HTTP_5000"] || "",
  [plasma.id]: process.env["NEXT_PUBLIC_RPC_HTTP_9745"] || "",
  [monad.id]: process.env["NEXT_PUBLIC_RPC_HTTP_143"] || "",
}
